import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { getAppUsers } from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import type { AppUser } from '../../types';
import { PageHeader, Alert, Button, DataTable } from '@racing/ui';

const AppUsersPage = () => {
  const navigate = useNavigate();
  const [appUsers, setAppUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAppUsers = useCallback(async () => {
    try {
      setLoading(true);
      setAppUsers(await getAppUsers());
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAppUsers();
  }, [loadAppUsers]);

  const columns = useMemo<ColumnDef<AppUser, any>[]>(
    () => [
      { accessorKey: 'name', header: 'Name', meta: { width: 170 } },
      { accessorKey: 'email', header: 'Email', meta: { width: 220 } },
      { accessorKey: 'first_name', header: 'First Name', meta: { width: 140, priority: 'md' } },
      { accessorKey: 'last_name', header: 'Last Name', meta: { width: 140, priority: 'md' } },
      { accessorKey: 'phone_number', header: 'Phone', meta: { width: 150, priority: 'lg' } },
      { accessorKey: 'city', header: 'City', meta: { width: 140, priority: 'lg' } },
      { accessorKey: 'state_province', header: 'State/Province', meta: { width: 150, priority: 'lg' } },
    ],
    []
  );

  return (
    <div className="flex min-w-0 flex-col">
      <PageHeader
        title="App Users"
        subtitle="Driver accounts from the registration app. These do not sign in to Control Center."
        actions={
          <Button className="w-full sm:w-auto" onClick={() => navigate('/admin/app-users/new')}>
            <Plus /> Create App User
          </Button>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <DataTable
        data={appUsers}
        columns={columns}
        getRowId={(row) => String(row.id)}
        loading={loading}
        onRowClick={(row) => navigate(`/admin/app-users/${row.id}`)}
        emptyMessage="No app users yet"
        pageSizeOptions={[10, 25, 50]}
        className="h-[calc(100dvh-16rem)] min-h-[22rem]"
      />
    </div>
  );
};

export default AppUsersPage;
